import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon, XIcon } from 'lucide-react';
import { degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
import { degreeProgramCombos } from '../utils/degreeSlugs';
import { usePageMeta } from '../hooks/usePageMeta';
const ANY = '';
const PAGE_SIZE = 60;

/**
 * Index of every degree level × program landing page.
 *
 * There are several hundred combinations, so this is a search-first page: the
 * three selects narrow by the same codes the match widget uses, and the text
 * box searches program and area names. Results are capped and paged rather
 * than rendering every combination at once.
 */
export function FindDegreesIndexPage() {
  usePageMeta('Find Online Degree Programs | Online College Guide', 'Search accredited online degree programs by level, area of study, and specialization. Get matched free by career goals, schedule, and budget.');
  const [query, setQuery] = useState('');
  const [degree, setDegree] = useState(ANY);
  const [area, setArea] = useState(ANY);
  const [program, setProgram] = useState(ANY);
  const [visible, setVisible] = useState(PAGE_SIZE);

  // The program list narrows to the chosen area, the same way the match
  // widget behaves, so the third select never offers an impossible pairing.
  const programOptions = useMemo(() => area ? specializationOptions.filter(s => s.focusValue === area) : specializationOptions, [area]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return degreeProgramCombos.filter(combo => {
      if (degree && combo.degreeInterest !== degree) return false;
      if (area && combo.areaStudy !== area) return false;
      if (program && combo.mProgram !== program) return false;
      if (!q) return true;
      return `${combo.title} ${combo.areaLabel}`.toLowerCase().includes(q);
    });
  }, [query, degree, area, program]);
  const isFiltered = Boolean(query.trim() || degree || area || program);
  const resetVisible = () => setVisible(PAGE_SIZE);
  const clearFilters = () => {
    setQuery('');
    setDegree(ANY);
    setArea(ANY);
    setProgram(ANY);
    resetVisible();
  };
  const selectClass = 'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors disabled:bg-gray-50 disabled:text-gray-400';
  return <main className="bg-white" data-id="element-1590" data-mp="30540vmvvw1pn">
      {/* Hero */}
      <header className="hero-bg" data-id="element-1591" data-mp="306orj1f2v0i8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1592" data-mp="2lghgv7vbals3">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1593" data-mp="2lemwpvrv6xp2">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-1594" data-mp="2qgcahj6h1pgu">
              <li data-id="element-1595" data-mp="2cfjdmrsp6iak">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1596" data-mp="2ch2xesdkbbh3">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1597" data-mp="1xt2tcoi1jmij">/</li>
              <li className="text-white/90" data-id="element-1598" data-mp="1xu43mqr8g4z7">Find Degrees</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl" data-id="element-1599" data-mp="1surlgjbb3m2y">
            Find Online Degree Programs
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-1600" data-mp="21uo15vfpxalv">
            Every degree level and specialization we match against, each with
            its own page. Narrow by level, area of study, and program to find
            yours.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-id="element-1601" data-mp="2br147jx5l0ta">
        {/* Search + filters */}
        <div className="mb-8" data-id="element-1602" data-mp="2lingljl895xm">
          <label htmlFor="degree-search" className="sr-only" data-id="element-1603" data-mp="2ljq3lgffio89">
            Search programs
          </label>
          <div className="relative max-w-xl mb-4" data-id="element-1604" data-mp="2zj7x293o6pny">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" data-id="element-1605" data-mp="2ujd7c8vms64h" />
            <input id="degree-search" type="search" value={query} onChange={e => {
            setQuery(e.target.value);
            resetVisible();
          }} placeholder="Search programs — try “nursing” or “cyber security”" className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-1606" data-mp="3ejde1a8thvq6" />
          </div>

          <div className="grid sm:grid-cols-3 gap-3 max-w-3xl" data-id="element-1607" data-mp="34na4vkfjswtq">
            <div data-id="element-1608" data-mp="34lyzxhad9j11">
              <label htmlFor="filter-degree" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-1609" data-mp="34pmrwc05xxdn">
                Degree level
              </label>
              <select id="filter-degree" value={degree} onChange={e => {
              setDegree(e.target.value);
              resetVisible();
            }} className={selectClass} data-id="element-1610" data-mp="ow5ph4m9vqof">
                <option value={ANY} data-id="element-1611" data-mp="24fvg7jyrc1c8">All levels</option>
                {degreeInterestOptions.map(option => <option key={option.value} value={option.value} data-id="element-1612" data-mp="1zm0p3vgtok34">
                    {option.type}
                  </option>)}
              </select>
            </div>

            <div data-id="element-1613" data-mp="1qp03fwl5103d">
              <label htmlFor="filter-area" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-1614" data-mp="1qlcelaqn11uz">
                Area of study
              </label>
              <select id="filter-area" value={area} onChange={e => {
              setArea(e.target.value);
              setProgram(ANY);
              resetVisible();
            }} className={selectClass} data-id="element-1615" data-mp="1bqfx06wxv258">
                <option value={ANY} data-id="element-1616" data-mp="14f9tem7tmfih">All areas</option>
                {focusOptions.map(option => <option key={option.value} value={option.value} data-id="element-1617" data-mp="195qaqherila4">
                    {option.type}
                  </option>)}
              </select>
            </div>

            <div data-id="element-1618" data-mp="zg6p53zo0dbk">
              <label htmlFor="filter-program" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-1619" data-mp="zfniijfgtbcu">
                Program
              </label>
              <select id="filter-program" value={program} onChange={e => {
              setProgram(e.target.value);
              resetVisible();
            }} className={selectClass} data-id="element-1620" data-mp="1s5dzr1ce8td6">
                <option value={ANY} data-id="element-1621" data-mp="2pxq7kuk55mr">All programs</option>
                {programOptions.map(option => <option key={`${option.focusValue}-${option.value}`} value={option.value} data-id="element-1622" data-mp="6r9kw4sagh1t">
                    {option.type}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500" data-id="element-1623" data-mp="3qd9sxetpnoar">
            <span aria-live="polite" data-id="element-1624" data-mp="96puk7nf08ul">
              {results.length.toLocaleString()}{' '}
              {results.length === 1 ? 'program' : 'programs'}
            </span>
            {isFiltered && <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-navy font-medium hover:underline" data-id="element-1625" data-mp="3cmdf4u4g1bnr">
                <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1626" data-mp="3mcy924969s0c" />
                Clear filters
              </button>}
          </div>
        </div>

        {/* Results */}
        {results.length === 0 ? <div className="border border-gray-200 rounded-xl py-16 px-6 text-center" data-id="element-1627" data-mp="2tv42wd3ag8sh">
            <h2 className="text-lg font-bold text-navy mb-2" data-id="element-1628" data-mp="2ow9bwnv63dxa">
              No programs match that search
            </h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto" data-id="element-1629" data-mp="23fk9j8hzs7y4">
              Try a broader term or clear the filters, or get matched directly —
              it takes about three minutes.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-1630" data-mp="2de9sl4lmjyfy">
              <button type="button" onClick={clearFilters} className="px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-1631" data-mp="2hfbtrqz3qkkk">
                Show all programs
              </button>
              <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200" data-id="element-1632" data-mp="2w3gd8x5zb04e">
                Get Matched Free
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1633" data-mp="3a37q2ukreagt" />
              </Link>
            </div>
          </div> : <>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1634" data-mp="tg9el9oyog1b">
              {results.slice(0, visible).map(combo => <li key={combo.slug} data-id="element-1635" data-mp="pf7ekqn7zzya">
                  <Link to={`/find-degrees/${combo.slug}`} className="group flex items-start justify-between gap-3 h-full border border-gray-200 rounded-lg px-4 py-3 hover:border-navy hover:bg-light transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-1636" data-mp="pg8zrnxya2tq">
                    <span className="min-w-0" data-id="element-1637" data-mp="18ch0qfjxjrj8">
                      <span className="block text-sm font-semibold text-navy group-hover:underline" data-id="element-1638" data-mp="18b5t24nd5zmu">
                        {combo.title}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5" data-id="element-1639" data-mp="20ucsyrwprmxq">
                        {combo.areaLabel}
                      </span>
                    </span>
                    <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-navy" aria-hidden="true" data-id="element-1640" data-mp="267vswlcbega8" />
                  </Link>
                </li>)}
            </ul>

            {visible < results.length && <div className="text-center mt-8" data-id="element-1641" data-mp="2p79r5fh2ud6j">
                <button type="button" onClick={() => setVisible(n => n + PAGE_SIZE)} className="px-6 py-3 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-1642" data-mp="2p5yppmb3thil">
                  Show more ({(results.length - visible).toLocaleString()}{' '}
                  remaining)
                </button>
              </div>}
          </>}
      </div>

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-1643" data-mp="32wv2c3omxo40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-id="element-1644" data-mp="3mkzou34mkhvf">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-1645" data-mp="3rjszlnsj1urw">
            Not sure which program fits?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7" data-id="element-1646" data-mp="3dktakf13rdv7">
            Answer a few questions about your goals, schedule, and budget and
            we'll narrow it down for you. Free, with no obligation.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-sm transition-colors duration-200" data-id="element-1647" data-mp="33tgltwalp3te">
            Get Matched Free
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1648" data-mp="2yuvt0hxohmez" />
          </Link>
        </div>
      </section>
    </main>;
}